import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Clock, Users, CheckSquare, AlertTriangle, Activity, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { dashboardApi } from '../services/eztaskApi';
import { toast } from 'sonner';

export default function EzTaskDashboard() {const { t } = useTranslation("eztask");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await dashboardApi.get();
        setData(result);
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>);

  }

  const priorityColors = {
    low: 'bg-green-500',
    medium: 'bg-yellow-500',
    high: 'bg-orange-500',
    urgent: 'bg-red-500'
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="eztask-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-400" />{t("eztaskDashboard", "EzTask Dashboard")}

            </h1>
            <p className="text-slate-400 mt-1">{t("overviewOfYour", "Overview of your project management metrics")}</p>
          </div>
          <Button onClick={() => navigate('/eztask')}>{t("viewAllBoards", "View All Boards")}</Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Boards"
            value={data?.total_boards || 0}
            icon={<BarChart3 className="w-5 h-5" />}
            color="blue" />
          
          <StatCard
            title="Total Cards"
            value={data?.total_cards || 0}
            icon={<CheckSquare className="w-5 h-5" />}
            color="green" />
          
          <StatCard
            title="Due Soon"
            value={data?.due_soon?.length || 0}
            icon={<Clock className="w-5 h-5" />}
            color="yellow"
            subtitle="Next 7 days" />
          
          <StatCard
            title="Team Members"
            value={data?.workload?.length || 0}
            icon={<Users className="w-5 h-5" />}
            color="purple" />
          
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cards by List */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />{t("cardsByList", "Cards by List")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.cards_by_list?.length > 0 ?
              <div className="space-y-3">
                  {data.cards_by_list.map((item, index) => {
                  const maxCount = Math.max(...data.cards_by_list.map((i) => i.count));
                  const percentage = maxCount > 0 ? item.count / maxCount * 100 : 0;
                  return (
                    <div key={index}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-300">{item.list}</span>
                          <span className="text-slate-400">{item.count}{t("cards", "cards")}</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${percentage}%` }} />
                        
                        </div>
                      </div>);

                })}
                </div> :

              <p className="text-slate-500 text-center py-8">{t("noDataAvailable", "No data available")}</p>
              }
            </CardContent>
          </Card>

          {/* Due Soon */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />{t("dueSoon", "Due Soon")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.due_soon?.length > 0 ?
              <div className="space-y-3 max-h-64 overflow-y-auto">
                  {data.due_soon.map((card, index) =>
                <div
                  key={index}
                  className={`p-3 rounded-lg ${card.is_overdue ? 'bg-red-900/30 border border-red-700' : 'bg-slate-700/50'}`}>
                  
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{card.title}</p>
                          <p className="text-sm text-slate-400">{card.list_name}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${card.is_overdue ? 'bg-red-500 text-white' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {card.is_overdue ? 'Overdue' : new Date(card.due_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                )}
                </div> :

              <p className="text-slate-500 text-center py-8">{t("noCardsDue", "No cards due soon")}</p>
              }
            </CardContent>
          </Card>

          {/* Team Workload */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />{t("teamWorkload", "Team Workload")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.workload?.length > 0 ?
              <div className="space-y-3">
                  {data.workload.map((member, index) => {
                  const maxCards = Math.max(...data.workload.map((m) => m.cards));
                  const percentage = maxCards > 0 ? member.cards / maxCards * 100 : 0;
                  return (
                    <div key={index} className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold">
                          {member.member?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-300">{member.member}</span>
                            <span className="text-slate-400">{member.cards}{t("cards", "cards")}</span>
                          </div>
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                            className="h-full bg-purple-500 rounded-full transition-all"
                            style={{ width: `${percentage}%` }} />
                          
                          </div>
                        </div>
                      </div>);

                })}
                </div> :

              <p className="text-slate-500 text-center py-8">{t("noWorkloadData", "No workload data")}</p>
              }
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-400" />{t("recentActivity", "Recent Activity")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.recent_activity?.length > 0 ?
              <div className="space-y-3 max-h-64 overflow-y-auto">
                  {data.recent_activity.map((activity, index) =>
                <div key={index} className="flex items-start gap-3 p-2 rounded hover:bg-slate-700/50">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Activity className="w-4 h-4 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-300">
                          <span className="font-medium text-white">{activity.actor}</span>{' '}
                          {activity.description}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                )}
                </div> :

              <p className="text-slate-500 text-center py-8">{t("noRecentActivity", "No recent activity")}</p>
              }
            </CardContent>
          </Card>
        </div>
      </div>
    </div>);

}

function StatCard({ title, value, icon, color, subtitle }) {const { t } = useTranslation("eztask");
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    purple: 'bg-purple-500/20 text-purple-400',
    red: 'bg-red-500/20 text-red-400'
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>);

}